
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Typography, Tooltip, Badge, Tag, Select } from 'antd';
import _, { isEmpty } from 'lodash';
import TeamsAPI from 'restAPI/TeamsAPI';
import { CellRenderers, getFilterProps, Sorters } from 'utils/tableUtils';
import ModalUtil from 'utils/modalUtil';
import { defaultRolesDisplayName, ROLE_TENANTADMIN } from 'constants/RBACConstants';
import ViewPermissionsModal from './../Roles/ViewPermissionsModal';
import classnames from 'classnames';
import { TableContainer } from 'components/TableContainer';
import Vocabulary from 'components/Vocabulary';
import UserAPI from 'restAPI/UserAPI';
import { ALL_OWNER_TYPES, TENANT_ADMIN_TOOLTIP } from '../Users/UserTeamsConstant';


// This component is used in product/subproduct form to show the members of the selected team and allowing to set
// product/subproduct specific owners. The individual owner fields are hidden in the form, therefore form.setFieldsValue is working
const TeamMemberDetails = ({ teamId, form, readOnly }) => {
    const [members, setMembers ] = useState([])
    const [loading, setLoading ] = useState(false);

    const setFilterValue = useCallback((item, type, obj) => {
        if (!item[type]) {
            item[type] = [];
        }
        item[type].push({text: obj.name, value: obj.name});
        return item;
    }, [])

    const existingOwnersOfEntityRef = useRef({});

    useEffect(() => {
        // Computing this only once as the form instance can change and we don't want this to compute again and again
        // if(_.isEmpty(existingOwnersOfEntityRef.current)){
            const formValues = form.getFieldsValue();
            const owners = {};
            ALL_OWNER_TYPES.forEach(type => owners[type] = formValues[type]);
            existingOwnersOfEntityRef.current = owners;
        // }
    // }, [form])

        setLoading(true);
        let cancel1, cancel2;

        const { promise: allUsersPromise, cancel } = UserAPI.getAll(undefined, "TENANT");
        cancel1 = cancel;
        
        let teamUsersPromise = Promise.resolve();
        if (teamId) {
            const { promise, cancel } = TeamsAPI.getUsersFromTeam(teamId);
            teamUsersPromise = promise;
            cancel2 = cancel;
        }

        allUsersPromise
        .then((usersWithEmailId = []) => {
            const emailList = usersWithEmailId.map(user => user.email);
            return UserAPI.getUsers({ filters: { email: emailList }, size: emailList.length }).promise;
        })
        .then(userDetails => {
            return Promise.all([Promise.resolve(userDetails.data.content), teamUsersPromise]);
        })
        .then(([tenantUsers, res = {}]) => {
            const tenantLevel = tenantUsers
            .map(entry => ({
                ...entry,
                user: {
                    id: entry.userId,
                    name: entry.name,
                },
                role: {
                    id: undefined,
                    name: entry.tenantRole,
                },
            }));
          
          const data = [...(res?.members || []), ...tenantLevel];
          
          _.forEach(data, (d) => d.responsibilities = []);
          ALL_OWNER_TYPES.forEach(key => {
              // if the product/subproduct has the type of owner, then no need to add the responsibility
              if(existingOwnersOfEntityRef.current[key]){
                  return;
              }
              if (!isEmpty(res[key])) {
                  const owner = res[key];
                  const index = data.findIndex(d => d.user?.id === owner.id);
                  if (index !== -1) {
                      data[index].responsibilities.push({
                          label: <Vocabulary name={key}/>,
                          value: key
                      });
                  }
              }
          })
          // update the members w.r.t the product/subproduct owners
          _.forEach(existingOwnersOfEntityRef.current, (value, key) => {
              if(!value){
                  return;
              }
              const record = _.find(data, d => d.user.id === value);
              // don't do anything if the owner is not present in the members list
              if(record){
                  record.responsibilities.push({
                      label: <Vocabulary name={key}/>,
                      value: key
                  })
              }
          })
          setMembers(data);
        }, e => {
          console.log(e);
        }).finally(() => {
            setLoading(false);
        })
        
        return () => {
            cancel1 && cancel1();
            cancel2 && cancel2();
        };
    }, [teamId, setFilterValue, form])

    const handleOnClick = useCallback((role) => {
        if (role.name === ROLE_TENANTADMIN) {
            return;
        }
        ModalUtil.show({
            content: <ViewPermissionsModal  role={{...role, role: role.name}}  />,
        });
    }, [])

    const onResponsibilityClear = useCallback((e) => {
        // antd hides the tag by default which causes the next tag to hide as we remove the current tag
        e.preventDefault();
        const type = e.currentTarget.parentNode.dataset.type;
        const userId = +e.currentTarget.parentNode.dataset.userid;

        // clearing the owner field in the form
        form.setFieldsValue({ [type]: null });

        // Updating the member list and removing the responsibility from the user
        setMembers(([...members]) => {
            const record = _.find(members, (m) => m.user.id === userId);
            _.remove(record.responsibilities, { value: type });
            return members;
        })
    }, [form]);
    
    const addResponsibility = useCallback((type, userId) => {

        // Setting the owner field in the form so that it is automatically saved on submit
        form.setFieldsValue({
            [type]: userId
        });

        // Updating the responsibilties of the user with the selected ownership
        setMembers(([...members]) => {
            const oldUser = _.find(members, m => _.find(m.responsibilities, {value: type}));
            if(oldUser){
                const responsibilityIndex = _.findIndex(oldUser.responsibilities, {value: type});
                if(responsibilityIndex > -1){
                    oldUser.responsibilities.splice(responsibilityIndex, 1);
                }
            }
            const record = _.find(members, (m) => m.user.id === userId);
            record.responsibilities.push({ value: type, label: <Vocabulary name={type}/> });
            return members;
        });
    }, [form]);

    const columns = useMemo(() => {
        return [
            {
                title: "Name",
                dataIndex: "user",
                key:"user",
                render: CellRenderers.NAME_ID_MAP,
                sorter: Sorters.STRING("user.name"),
                ...getFilterProps({
                    dataIndex: "user",
                    dataSource: members,
                })
              },
              {
                  title: 'Responsibilities',
                  dataIndex: "responsibilities",
                  render: (responsibilities, record) => {
                    const userId = record.user.id;
                      return (
                        <>
                            {
                                _.map(responsibilities, (responsibility, i) => {
                                    return (
                                        <div key={responsibility.type} className={classnames("p-b-xs", {"p-t-xs border-top": i !== 0})}>
                                            {
                                                <Tag key={responsibility.type} data-type={responsibility.value} data-userid={userId} closable={!readOnly} onClose={onResponsibilityClear}>
                                                   <Vocabulary name={responsibility.label}/>
                                                </Tag>
                                            }
                                        </div>
                                    )
                                })
                            }
                            {
                                !readOnly
                                ?
                                <div className={_.size(responsibilities) ? 'p-t-xs border-top' : ""}>
                                <Select
                                    value={null}
                                    placeholder="Add Responsibility"
                                    onChange={(type) =>
                                        addResponsibility(type, userId)
                                    }
                                    size="small"
                                    options={
                                        ALL_OWNER_TYPES.filter((value) => _.findIndex(responsibilities, { value }) < 0)
                                        .map((value) => ({
                                            label: <Vocabulary name={value} />,
                                            value,
                                        }))}
                                    style={{ width: 160 }}
                                    notFoundContent="No Responsibility available"
                                />
                                </div>
                                :
                                _.isEmpty(responsibilities) ? CellRenderers.VALUE_OR_NA() : null
                            }
                        </>
                      )
                  },
                  default: true
              },
              {
                title: 'Roles',
                dataIndex: 'role',
                key: 'role',
                render: (role, original) => {
                    return (
                        <Tooltip title={role.name === ROLE_TENANTADMIN? TENANT_ADMIN_TOOLTIP : ""}>
                            <Typography.Link onClick={() => handleOnClick(role)}>{CellRenderers.VALUE_OR_NA(defaultRolesDisplayName[role.name] ?? role.name)}</Typography.Link>
                        </Tooltip>
                    )
                },
                sorter: Sorters.STRING("role.name")
              }
            ]
    }, [handleOnClick, members, addResponsibility, onResponsibilityClear, readOnly]);

    return (
        <TableContainer
            title={<>Members <Badge count={members.length} className="no-border" style={{backgroundColor: '#35198e'}} /></>}
            className="table-container m-b-l"
            loading={loading}
            columns={columns}
            dataSource={members}
            defaultSort={{column: "user", order: "ascend"}}
            rowKey="user.id"
            locale={{emptyText: "No Members"}}
        />
    )
  }

  export default TeamMemberDetails;